// QueryToken.tsx
import type React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import type { AppDispatch, RootState } from '../../store/store';
import store from '../../store/store';
import { isCompanyBrandingComplete } from '../../utils/branding';
import { canAccessSetup, isPublicRoute, ROUTES } from '../../utils/routes';
import { getSetupProgress } from '../../utils/setup';
import { getUserFonctionTypesForCompany } from '../../utils/users';
import { checkJwt } from './thunks';

function isSetupComplete(state: RootState): boolean {
  const company = state.auth.company;
  if (!company) {
    return true;
  }
  const { percent } = getSetupProgress(company, isCompanyBrandingComplete(company));
  if (percent < 100) {
    return false;
  }
  const hasPlanningConfigured = Boolean(company.toolsTimeline && Object.keys(company.toolsTimeline).length > 0);
  if (!hasPlanningConfigured) {
    return false;
  }
  return Boolean(company.mandatoryNewsletterSentAt);
}

// The "Paramétrage de votre compte" redirect is only relevant to users who can
// actually configure the account. Standard users (e.g. agent commercial,
// transactionnaire) must never be forced into the setup flow.
function shouldRedirectToParametrage(state: RootState): boolean {
  const fonctionTypes = getUserFonctionTypesForCompany(state.auth.user, state.auth.company?.id);
  if (!canAccessSetup(fonctionTypes, state.auth.isAdmin)) {
    return false;
  }
  return !isSetupComplete(state);
}

const QueryToken: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentCompany = useSelector((state: RootState) => state.auth.company);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleJwtCheck = async() => {
      const params = new URLSearchParams(location.search);
      const accessToken = params.get('accessToken');
      const companyFromDJ = Number(params.get('company'));

      const isInitialLogin = Boolean(accessToken && companyFromDJ);

      const isLogged = await dispatch(checkJwt(accessToken, companyFromDJ, currentCompany));

      if (location.pathname !== '/blocked-access' && !isPublicRoute(location.pathname) && !isLogged) {
        navigate(ROUTES.BLOCKED_ACCESS, { replace: true });
        return;
      }
      if (location.pathname === '/blocked-access' && isLogged) {
        if (shouldRedirectToParametrage(store.getState())) {
          navigate(ROUTES.NEW_DASHBOARD_PARAMETRAGE, { replace: true });
        } else {
          navigate(ROUTES.HOME, { replace: true });
        }
        return;
      }

      if (isInitialLogin && isLogged && shouldRedirectToParametrage(store.getState())) {
        params.delete('accessToken');
        params.delete('company');
        const remaining = params.toString();
        const target = remaining
          ? `${ROUTES.NEW_DASHBOARD_PARAMETRAGE}?${remaining}`
          : ROUTES.NEW_DASHBOARD_PARAMETRAGE;
        navigate(target, { replace: true });
        return;
      }

      // Strip only JWT-related query params, preserving app params like ?step=
      const hadJwtParams = params.has('accessToken') || params.has('company');
      if (hadJwtParams) {
        params.delete('accessToken');
        params.delete('company');
        const remaining = params.toString();
        navigate(remaining ? `${location.pathname}?${remaining}` : location.pathname, { replace: true });
      }
    };

    handleJwtCheck();
  }, [location, dispatch, navigate]);

  return null;
};

export default QueryToken;
